/**
 * Bookshop Phase B — Uploads console.
 * Mobile-friendly: drag-drop on desktop, native file picker (with camera
 * option) on phones/tablets. Copies asset URLs to clipboard so admins can
 * paste them into a product's `asset_url` / `cover_image_url`.
 */
import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, UploadCloud, Trash2, Copy, Camera, Image as ImageIcon, Music, Video, FileText, Lock } from 'lucide-react';
import api from '@/lib/api';

const KIND_META = {
  images:  { label: 'Images',  icon: ImageIcon, accept: 'image/*',                                    max: '20 MB' },
  books:   { label: 'Books',   icon: FileText,  accept: 'application/pdf,application/epub+zip',        max: '100 MB' },
  audio:   { label: 'Audio',   icon: Music,     accept: 'audio/*',                                     max: '200 MB' },
  video:   { label: 'Video',   icon: Video,     accept: 'video/*',                                     max: '500 MB' },
  private: { label: 'Private', icon: Lock,      accept: '*/*',                                         max: '500 MB' },
};

const bytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'], i = Math.min(3, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
};

export const Uploads = () => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [kind, setKind] = useState('');
  const [caption, setCaption] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [progress, setProgress] = useState(null);   // 0..100 while uploading

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-uploads', kind],
    queryFn: async () => (await api.get(`/bookshop/admin/uploads${kind ? `?kind=${kind}` : ''}`)).data,
  });
  const rows = data?.uploads || [];
  const counts = data?.counts || {};

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    if (kind) fd.append('kind', kind);
    if (caption) fd.append('caption', caption);
    if (isPrivate) fd.append('is_private', 'true');
    // Use XHR to get real-time progress (fetch's ReadableStream progress
    // isn't cross-browser yet).
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${base}/api/bookshop/admin/uploads`);
    // Auth token from the admin auth store lives on the axios instance's
    // interceptor. Peek it out for the raw XHR:
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      setProgress(null);
      try {
        const d = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300 && d.success) {
          toast.success(`Uploaded ${file.name}`);
          setCaption('');
          qc.invalidateQueries({ queryKey: ['bookshop-uploads'] });
        } else {
          toast.error(d.detail || `Upload failed (${xhr.status})`);
        }
      } catch (e) { toast.error('Upload response unreadable'); }
    };
    xhr.onerror = () => { setProgress(null); toast.error('Network error during upload'); };
    xhr.send(fd);
  };

  const onFiles = (files) => {
    Array.from(files || []).forEach(upload);
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onFiles(e.dataTransfer.files);
  };

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/bookshop/admin/uploads/${id}`)).data,
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-uploads'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  });

  const copyUrl = (url) => {
    const full = url.startsWith('http') ? url : `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${url}`;
    navigator.clipboard.writeText(full).then(
      () => toast.success('URL copied'),
      () => toast.error('Could not copy — long-press to select')
    );
  };

  const meta = kind ? KIND_META[kind] : { label: 'Any', accept: '*/*', max: '500 MB' };

  return (
    <div className="space-y-3" data-testid="bookshop-uploads">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><UploadCloud className="h-4 w-4" /> Upload from any device</CardTitle>
          <CardDescription className="text-xs">
            Drag & drop, tap to pick a file, or use your phone camera. Files land in <code>/uploads/bookshop/</code> and get a public URL you can paste into a product&apos;s <code>asset_url</code> or <code>cover_image_url</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-1 text-xs">
            <button className={`px-2 py-1 rounded border ${kind === '' ? 'bg-slate-900 text-white' : ''}`} onClick={() => setKind('')} data-testid="fwb-uploads-kind-all">All ({Object.values(counts).reduce((a, b) => a + b, 0)})</button>
            {Object.entries(KIND_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button key={k} className={`px-2 py-1 rounded border flex items-center gap-1 ${kind === k ? 'bg-slate-900 text-white' : ''}`} onClick={() => setKind(k)} data-testid={`fwb-uploads-kind-${k}`}>
                  <Icon className="h-3 w-3" /> {m.label} ({counts[k] || 0})
                </button>
              );
            })}
          </div>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300'}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="fwb-uploads-dropzone"
          >
            <UploadCloud className="mx-auto h-8 w-8 text-slate-400 mb-2" />
            <p className="text-sm">Drop files here <span className="text-slate-400">or</span></p>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              <input ref={fileRef} type="file" multiple accept={meta.accept} className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="fwb-uploads-input" />
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={progress !== null} data-testid="fwb-uploads-pick">
                <UploadCloud className="h-4 w-4 mr-1" /> Choose file(s)
              </Button>
              <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="fwb-uploads-camera-input" />
              <Button size="sm" variant="outline" onClick={() => cameraRef.current?.click()} disabled={progress !== null} data-testid="fwb-uploads-camera">
                <Camera className="h-4 w-4 mr-1" /> Use camera / gallery
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Kind: <strong>{meta.label}</strong> · max {meta.max}</p>
            {progress !== null && (
              <div className="mt-3 w-full bg-slate-200 rounded overflow-hidden" data-testid="fwb-uploads-progress">
                <div className="h-2 bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
                <p className="text-[11px] text-slate-600 mt-1">{progress}%</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Caption / alt-text (optional)</Label>
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Cover of Walking in Divine Purpose" data-testid="fwb-uploads-caption" />
            </div>
            <label className="flex items-center gap-2 text-xs mt-6">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} data-testid="fwb-uploads-private" />
              Mark as private (won&apos;t be exposed by content browsers)
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Existing uploads grid */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Library</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="animate-spin mx-auto" /> : rows.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-8">No uploads yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fwb-uploads-grid">
              {rows.map((u) => {
                const isImg = (u.content_type || '').startsWith('image/');
                const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
                const previewUrl = u.url.startsWith('http') ? u.url : `${base}${u.url}`;
                return (
                  <article key={u.id} className="border rounded p-2 flex flex-col text-xs" data-testid={`fwb-upload-${u.id}`}>
                    {isImg ? (
                      <img src={previewUrl} alt={u.caption || u.original_filename || ''} className="aspect-square object-cover rounded" />
                    ) : (
                      <div className="aspect-square rounded bg-slate-100 flex items-center justify-center text-slate-500">
                        {(KIND_META[u.kind]?.icon || FileText) && React.createElement(KIND_META[u.kind]?.icon || FileText, { className: 'h-8 w-8' })}
                      </div>
                    )}
                    <p className="truncate mt-1" title={u.original_filename}>{u.original_filename}</p>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                      <Badge variant="outline" className="text-[10px]">{u.kind}</Badge>
                      <span>{bytes(u.size_bytes)}</span>
                    </div>
                    <div className="flex gap-1 mt-1">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => copyUrl(u.url)} data-testid={`fwb-upload-copy-${u.id}`}><Copy className="h-3 w-3 mr-1" /> URL</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm('Delete this upload?')) remove.mutate(u.id); }} data-testid={`fwb-upload-del-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Uploads;
